import type { ApiErrorBody, Paged } from '@ovo/shared'

// Типизированная обёртка над fetch для /api/v1. Авторизация — Authorization: tma <initDataRaw>.
// В режиме VITE_API_MOCK=1 запросы обслуживает фейковый бэкенд в браузере (src/api/mock).

export type ApiErrorCode = ApiErrorBody['error']['code'] | 'NETWORK'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number

  constructor(code: ApiErrorCode, message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

const BASE = import.meta.env.VITE_API_BASE || '/api/v1'
export const API_MOCK = import.meta.env.VITE_API_MOCK === '1'

let authInitData: string | null = null
/** Сайт CRM в браузере (не Telegram): вход по cookie сессии. */
let webMode = false

export function setAuthInitData(raw: string | null): void {
  authInitData = raw
}

export function setWebMode(on: boolean): void {
  webMode = on
}

export function isWebMode(): boolean {
  return webMode
}

/** Выйти с сайта на этом компьютере. */
export async function webLogout(): Promise<void> {
  await fetch(`${BASE}/auth/web/logout`, { method: 'POST' }).catch(() => {})
  window.location.assign('/')
}

export type QueryValue = string | number | boolean | null | undefined
export type Query = Record<string, QueryValue>

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions {
  query?: Query
  body?: unknown
  signal?: AbortSignal
}

function buildQuery(query?: Query): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

const DEFAULT_MESSAGES: Record<ApiErrorCode, string> = {
  UNAUTHORIZED: 'Не удалось подтвердить вход — откройте приложение заново из Telegram',
  FORBIDDEN: 'Недостаточно прав для этого действия',
  NOT_FOUND: 'Не нашли — возможно, её уже удалили',
  VALIDATION: 'Проверьте заполненные поля',
  INVALID_STATE: 'Статус уже изменился — обновили данные',
  CONFLICT: 'Кто-то успел внести изменения — обновили данные',
  INTERNAL: 'Что-то пошло не так на сервере — попробуйте ещё раз',
  NETWORK: 'Нет соединения — проверьте интернет или VPN',
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 400 || status === 422) return 'VALIDATION'
  return 'INTERNAL'
}

const CYRILLIC = /[а-яё]/i

/** Сообщение для пользователя: русский текст сервера или наш по коду ошибки. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    return CYRILLIC.test(e.message) ? e.message : DEFAULT_MESSAGES[e.code]
  }
  return DEFAULT_MESSAGES.INTERNAL
}

export async function request<T>(method: Method, path: string, opts: RequestOptions = {}): Promise<T> {
  if (API_MOCK) {
    const { mockRequest } = await import('./mock/server.ts')
    return mockRequest<T>(method, path, opts.query, opts.body)
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (authInitData) headers.Authorization = `tma ${authInitData}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`${BASE}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError('NETWORK', DEFAULT_MESSAGES.NETWORK, 0)
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }
  // Сессия сайта закончилась — перезагрузка покажет вход
  if (res.status === 401 && webMode) window.location.reload()
  if (!res.ok) {
    const body = data as Partial<ApiErrorBody> | null
    const code = body?.error?.code ?? statusToCode(res.status)
    throw new ApiError(code, body?.error?.message || DEFAULT_MESSAGES[code], res.status)
  }
  return data as T
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
}

/** Списочные ответы: массив или Paged<T> — контракт для части эндпоинтов не зафиксирован. */
export function toList<T>(data: T[] | Paged<T> | { items: T[] } | null | undefined): T[] {
  if (!data) return []
  return Array.isArray(data) ? data : data.items
}
